import { EMPTY_TABLE_LIST } from "@shared/config/constants";
import { cn } from "@shared/lib/utils";
import { Button } from "@shared/ui/button";
import { Skeleton } from "@shared/ui/skeleton";
import {
	TableBody,
	TableCell,
	Table as TableContainer,
	TableHead,
	TableHeader,
	TableRow,
} from "@shared/ui/table";
import { type ColumnDef, flexRender, type Table } from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { FormattedMessage } from "react-intl";
import { TableEmpty } from "./table-empty";

type TableContentProps<T> = {
	isPending: boolean;
	table: Table<T>;
	isEmpty: boolean;
	enhancedColumns: ColumnDef<T, unknown>[];
	onCreate: () => void;
};

export function TableContent<T>({
	isPending,
	table,
	enhancedColumns,
	isEmpty,
	onCreate,
}: TableContentProps<T>) {
	return (
		<div className="overflow-x-auto rounded-lg border border-border bg-card">
			<TableContainer>
				<TableHeader className="bg-secondary/40">
					{table.getHeaderGroups().map((hg) => (
						<TableRow key={hg.id} className="border-border hover:bg-transparent">
							{hg.headers.map((header, idx) => {
								const isActions = header.column.id === "actions";
								const isSelect = header.column.id === "__select";
								const canSort = header.column.getCanSort();
								const sorted = header.column.getIsSorted();
								return (
									<TableHead
										key={header.id}
										className={cn(
											"h-11 text-xs font-medium uppercase tracking-wider text-muted-foreground",
											isActions && "text-left",
											isSelect && "w-10",
											idx === 0 && "pl-4"
										)}
									>
										{header.isPlaceholder ? null : canSort ? (
											<Button
												onClick={header.column.getToggleSortingHandler()}
												className="inline-flex p-0! text-white items-center gap-1.5 transition-colors hover:text-foreground cursor-pointer no-underline hover:no-underline"
												variant="link"
											>
												{flexRender(header.column.columnDef.header, header.getContext())}
												{sorted === "asc" ? (
													<ArrowUp className="h-3.5 w-3.5" />
												) : sorted === "desc" ? (
													<ArrowDown className="h-3.5 w-3.5" />
												) : (
													<ArrowUpDown className="h-3.5 w-3.5 opacity-40" />
												)}
											</Button>
										) : (
											flexRender(header.column.columnDef.header, header.getContext())
										)}
									</TableHead>
								);
							})}
						</TableRow>
					))}
				</TableHeader>
				<TableBody>
					{isPending &&
						EMPTY_TABLE_LIST.map((elem) => (
							<TableRow key={`sk-${elem}`} className="border-border">
								{enhancedColumns.map((column) => (
									<TableCell key={column.id}>
										<Skeleton className="h-4 w-full max-w-[140px]" />
									</TableCell>
								))}
							</TableRow>
						))}

					{!isPending && isEmpty && (
						<TableRow className="hover:bg-transparent">
							<TableCell colSpan={enhancedColumns.length} className="h-64">
								<TableEmpty onAdd={onCreate} />
							</TableCell>
						</TableRow>
					)}

					{!isPending && !isEmpty && table.getRowModel().rows.length === 0 ? (
						<TableRow className="hover:bg-transparent">
							<TableCell
								colSpan={enhancedColumns.length}
								className="h-32 text-center text-sm text-muted-foreground"
							>
								<FormattedMessage
									id="table.noSearchResults"
									defaultMessage="No results match your search."
								/>
							</TableCell>
						</TableRow>
					) : (
						table.getRowModel().rows.map((row, idx) => (
							<TableRow
								key={row.id}
								data-state={row.getIsSelected() ? "selected" : undefined}
								className={cn(
									"border-border transition-colors duration-150",
									idx % 2 === 0 ? "hover:odd:bg-muted" : "hover:bg-transpernet",
									"data-[state=selected]:bg-primary/10",
									"odd:bg-muted"
								)}
							>
								{row.getVisibleCells().map((cell, ci) => {
									const isActions = cell.column.id === "actions";
									return (
										<TableCell
											key={cell.id}
											className={cn(
												"py-3 text-sm",
												ci === 0 && "pl-4",
												isActions && "pr-4 text-right"
											)}
										>
											{isActions ? (
												<div className="flex justify-end">
													{flexRender(cell.column.columnDef.cell, cell.getContext())}
												</div>
											) : (
												flexRender(cell.column.columnDef.cell, cell.getContext())
											)}
										</TableCell>
									);
								})}
							</TableRow>
						))
					)}
				</TableBody>
			</TableContainer>
		</div>
	);
}
